import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from '../../Images/ArchiveRestore.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import plantTypeService from '../../services/plant-type.service';

function ArchivePlantTypeList() {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);

  useEffect(() => {
    fetchArchiveList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pagesize, search, orderBy]);

  const fetchArchiveList = async () => {
    setLoading(true);
    try {
      const response = await plantTypeService.getPlantTypeArchiveList(
        page,
        pagesize,
        search || undefined,
        orderBy || undefined,
      );
      if (response.data) {
        const plantTypes = response.data.results?.data || response.data.results || response.data || [];
        setData(Array.isArray(plantTypes) ? plantTypes : []);
        setTotalRecord(response.data.count || plantTypes.length || 0);
      }
    } catch (error) {
      message.error('Failed to fetch archived plant types');
      console.error('Error fetching archived plant types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = e => {
    setSearch(e.target.value);
    setPage(1);
  };

  const resetFilter = () => {
    setSearch('');
    setOrderBy('');
    setPage(1);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'code' && sortOrder === 'ascend') {
      sortingEnumKey = 'code';
    }
    if (sortField === 'code' && sortOrder === 'descend') {
      sortingEnumKey = '-code';
    }
    if (sortField === 'name' && sortOrder === 'ascend') {
      sortingEnumKey = 'name';
    }
    if (sortField === 'name' && sortOrder === 'descend') {
      sortingEnumKey = '-name';
    }
    if (sortField === 'updated_at' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_at';
    }
    if (sortField === 'updated_at' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_at';
    }

    return sortingEnumKey;
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  };

  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1,
  }));

  const handleRestore = async id => {
    if (!id) {
      message.error('Missing ID for restore!');
      return;
    }

    try {
      await plantTypeService.restorePlantType(id);
      message.success('Plant Type restored successfully');
      setSelectedRowKeys([]);
      fetchArchiveList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to restore plant type';
      message.error(errorMessage);
    }
  };

  const handleBulkRestore = async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.error('Please select at least one record to restore');
      return;
    }

    try {
      await plantTypeService.restorePlantType(selectedRowKeys);
      message.success(
        `${selectedRowKeys.length} Plant Type(s) restored successfully`,
      );
      setSelectedRowKeys([]);
      fetchArchiveList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to restore plant types';
      message.error(errorMessage);
    }
  };

  const handleView = useCallback(record => {
    navigate(`/view-archive-plant-type/${record.id}`);
  }, [navigate]);

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      sorter: false,
      width: 80,
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: true,
      width: 120,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 200,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      width: 150,
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: false,
      width: 150,
      render: (text, record) => {
        const createdByInfo = record?.created_by_info;
        if (createdByInfo && typeof createdByInfo === 'object') {
          const firstName = createdByInfo.first_name?.replace(/[()']/g, '').trim() || '';
          const lastName = createdByInfo.last_name?.trim() || '';
          return (
            <span>
              {firstName} {lastName}
            </span>
          );
        }
        return <span>N/A</span>;
      },
    },
    {
      title: 'Archive At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      width: 150,
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Archive By',
      dataIndex: 'updated_by_info',
      key: 'updated_by_info',
      sorter: false,
      width: 150,
      render: (text, record) => {
        const updatedByInfo = record?.updated_by_info;
        if (updatedByInfo && typeof updatedByInfo === 'object') {
          const firstName = updatedByInfo.first_name?.replace(/[()']/g, '').trim() || '';
          const lastName = updatedByInfo.last_name?.trim() || '';
          return (
            <span>
              {firstName} {lastName}
            </span>
          );
        }
        return <span>N/A</span>;
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <span
              onClick={() => handleView(record)}
              style={{ cursor: 'pointer', marginRight: '8px' }}
            >
              <EyeOutlined style={{ fontSize: '17px' }} />
            </span>
          </Tooltip>
          <Tooltip placement="top" title="Restore">
            <Popconfirm
              title="Are you sure you want to restore this plant type?"
              onConfirm={() => handleRestore(record.id)}
              onCancel={() => message.info('Action cancelled')}
              okText="Yes"
              cancelText="No"
            >
              <ArchiveRestore className="ArchiverestorImg" style={{ cursor: 'pointer' }} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: selectedKeys => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  return (
    <>
      <div className="sticyaction">
        <Row className="pagenamerow" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Archived Plant Types</h2>
            <div className="bredcrumbwrp">
              <Link to="/plant-type-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/plant-type-list">Plant Type Master</Link> },
                  { title: 'Archived Plant Types' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="large">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={search}
                style={{ width: 200 }}
                onChange={handleSearch}
              />
              <Tooltip
                title={
                  selectedRowKeys.length === 0
                    ? 'Select at least one record to restore'
                    : `Restore ${selectedRowKeys.length} selected record(s)`
                }
              >
                <Popconfirm
                  title={`Are you sure you want to restore ${selectedRowKeys.length} selected plant type(s)?`}
                  onConfirm={handleBulkRestore}
                  onCancel={() => message.info('Action cancelled')}
                  okText="Yes"
                  cancelText="No"
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button
                    type="primary"
                    disabled={selectedRowKeys.length === 0}
                  >
                    Restore Selected
                  </Button>
                </Popconfirm>
              </Tooltip>
              <Tooltip placement="top" title={'Reset Filter'}>
                <Button
                  type="primary"
                  disabled={!search && !orderBy}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </div>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="id"
            rowSelection={rowSelection}
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
            style={{ marginTop: '20px' }}
          />
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>
    </>
  );
}

export default ArchivePlantTypeList;

